import {useEffect, useState} from 'react';
import {NextPage} from 'next';
import {useRouter} from 'next/router';

import {
  PaymentMethodType,
  useProfilePageQuery,
  useSendPaymentMethodUpdateLinkMutation,
} from '#apollo/schema';
import {Screen} from '#component/base';
import {FullscreenLoading} from '#component/partial';
import {withRequireUserState} from '#component/util/RequireUserState/RequireUserState';
import {useToastQueue} from '#hook';

import ProfileHeader from './components/ProfileHeader';
import {EditPaymentConfirmationModal} from './components/SubscriptionTab/InfoBoxes/components/PaymentInfoBox/components/EditPaymentConfirmationModal';
import {EditPaymentLoadingModal} from './components/SubscriptionTab/InfoBoxes/components/PaymentInfoBox/components/EditPaymentLoadingModal';
import {handleRenderTabs, parseProfilePageQuery, parseQueryParams, tabMapper} from './Profile.util';

import {containerProfile} from './Profile.css';

export const ProfileScreen: NextPage = () => {
  const router = useRouter();
  const {addToast} = useToastQueue();
  const {shouldSendUpdatePaymentEmail} = parseQueryParams(router.query);
  const profilePageQuery = useProfilePageQuery({skip: !shouldSendUpdatePaymentEmail});
  const pageData = parseProfilePageQuery(profilePageQuery?.data);
  const [paymentUpdateConfirmModalOpen, setPaymentUpdateConfirmModalOpen] = useState(false);
  const [sendPaymentMethodUpdateLinkMutation, {loading: sendingPaymentMethodUpdateEmail}] =
    useSendPaymentMethodUpdateLinkMutation();

  let tab = (router.query.tab as string) || 'subscription';
  const keyTabs = Object.keys(tabMapper);
  tab = keyTabs.includes(tab) ? tab : 'subscription';
  const ComponentRendered = handleRenderTabs(tab as keyof typeof tabMapper);

  useEffect(() => {
    const sendUpdatePaymentEmail = async () => {
      try {
        const {data, errors} = await sendPaymentMethodUpdateLinkMutation();
        if (data?.sendPaymentMethodUpdateLink.success) {
          setPaymentUpdateConfirmModalOpen(true);
          return;
        }
        throw new Error(
          `Failed to send payment method update link: ${
            errors ? errors.join(', ') : 'Unknown error'
          }`,
        );
      } catch (error) {
        addToast({
          children: 'Uh oh, something went wrong!',
        });
        throw error;
      }
    };

    if (shouldSendUpdatePaymentEmail) {
      sendUpdatePaymentEmail();
    }
  }, [shouldSendUpdatePaymentEmail, sendPaymentMethodUpdateLinkMutation, addToast]);

  if (shouldSendUpdatePaymentEmail && profilePageQuery.loading) {
    return <FullscreenLoading />;
  }

  return (
    <Screen
      hero={<ProfileHeader />}
      headerProps={{variant: 'dark'}}
      contentContainerClassName={containerProfile}
      pageName="Profile">
      {<ComponentRendered />}

      {sendingPaymentMethodUpdateEmail && <EditPaymentLoadingModal />}
      <EditPaymentConfirmationModal
        paymentMethodType={pageData.paymentMethodType ?? PaymentMethodType.Creditcard}
        open={paymentUpdateConfirmModalOpen}
        onOpenChange={setPaymentUpdateConfirmModalOpen}
      />
    </Screen>
  );
};

export const Profile: NextPage = withRequireUserState(ProfileScreen, {userState: 'authenticated'});
